import { usePasswordValidation } from '../../../../../hooks/usePasswordValidation';
import { setChangePasswordModal } from '../../../../../store/workspace/slice';
import { changePasswordThunk } from '../../../../../store/auth/thunks';
import { ChangePasswordSuccessIcon, LockSquareIcon } from '../../../../../assets/icons';
import { useDispatch } from '../../../../../store/store';
import Button from '../../../../../components/Button';
import Modal from '../../../../../components/Modal';
import Input from '../../../../../components/Input';
import { useEffect, useState } from 'react';
import styles from './style.module.scss';

type Step = 'Form' | 'Done';
type ValidationState = 'NotValidated' | 'Invalid' | 'Valid';

export default function ChangePassword() {
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const [step, setStep] = useState<Step>('Form');
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const { min, num, char, word } = usePasswordValidation(newPassword);
    const [errorMessage, setErrorMessage] = useState('');

    const [minValidation, setMinValidation] = useState<ValidationState>('NotValidated');
    const [numValidation, setNumValidation] = useState<ValidationState>('NotValidated');
    const [charValidation, setCharValidation] = useState<ValidationState>('NotValidated');
    const [wordValidation, setWordValidation] = useState<ValidationState>('NotValidated');
    const [matchValidation, setMachValidation] = useState<ValidationState>('NotValidated');

    useEffect(() => {
        !min ? setMinValidation('NotValidated') : setMinValidation('Valid');
        !num ? setNumValidation('NotValidated') : setNumValidation('Valid');
        !char ? setCharValidation('NotValidated') : setCharValidation('Valid');
        !word ? setWordValidation('NotValidated') : setWordValidation('Valid');
        (!newPassword || (newPassword !== confirmPassword)) ? setMachValidation('NotValidated') : setMachValidation('Valid');
    }, [min, num, char, word, confirmPassword]);

    const next = () => {
        if (step === 'Done') {
            dispatch(setChangePasswordModal(false));
            return;
        }

        if ((!min || !num || !char || !word) || (newPassword !== confirmPassword)) {
            !min ? setMinValidation('Invalid') : setMinValidation('Valid');
            !num ? setNumValidation('Invalid') : setNumValidation('Valid');
            !char ? setCharValidation('Invalid') : setCharValidation('Valid');
            !word ? setWordValidation('Invalid') : setWordValidation('Valid');
            (!newPassword || (newPassword !== confirmPassword)) ? setMachValidation('Invalid') : setMachValidation('Valid');
            return;
        }

        setMinValidation('Valid');
        setNumValidation('Valid');
        setCharValidation('Valid');
        setWordValidation('Valid');
        setMachValidation('Valid');

        const body = {
            oldPassword,
            newPassword
        };

        setIsLoading(true);
        setErrorMessage('');

        dispatch(changePasswordThunk(body))
            .unwrap()
            .then(() => {
                setStep('Done');
                setIsLoading(false);
            })
            .catch(() => {
                setIsLoading(false);
                setErrorMessage('Old password is not correct.')
            })
    };

    return (
        <Modal
            size='sm'
            icon={<LockSquareIcon />}
            title='Change Login Password'
            childClassName={styles.wrapper}
            closeFunc={() => dispatch(setChangePasswordModal(false))}
        >
            {step === 'Form' && <div className={styles.form}>
                <Input
                    type='password'
                    value={oldPassword}
                    label='Old password'
                    onChange={setOldPassword}
                    hasError={Boolean(errorMessage)}
                />
                <Input
                    type='password'
                    value={newPassword}
                    label='New password'
                    onChange={setNewPassword}
                />
                <Input
                    type='password'
                    label='Confirm password'
                    value={confirmPassword}
                    onChange={setConfirmPassword}
                />
                <div className={styles.validation}>
                    <span
                        className={styles.validation_item}
                        data-validation-status={numValidation}
                    >
                        It must contain at least one number
                    </span>
                    <span
                        className={styles.validation_item}
                        data-validation-status={minValidation}
                    >
                        It must contain at least 8 characters
                    </span>
                    <span
                        className={styles.validation_item}
                        data-validation-status={wordValidation}
                    >
                        It must contain at least one capital and one samll letter
                    </span>
                    <span
                        className={styles.validation_item}
                        data-validation-status={charValidation}
                    >
                        It must contain at least one special character (!@#$%^&*)
                    </span>
                    <span
                        className={styles.validation_item}
                        data-validation-status={matchValidation}
                    >
                        The passwords must match
                    </span>
                </div>
            </div>}

            {step === 'Done' && <div className={styles.success}>
                <ChangePasswordSuccessIcon />
                <span>Your password has been changed successfully.</span>
            </div>}

            {errorMessage && <span className={styles.error}>{errorMessage}</span>}

            <div className={styles.actions}>
                {step === 'Form' && <Button
                    type='Outline-Primary'
                    text='Cancel'
                    disabled={isLoading}
                    className={styles.actions_btn}
                    onClick={() => dispatch(setChangePasswordModal(false))}
                />}
                <Button
                    type='Primary'
                    onClick={next}
                    loading={isLoading}
                    className={styles.actions_btn}
                    text={step === 'Form' ? 'Confirm' : 'Ok'}
                />
            </div>
        </Modal>
    );
};
